import { Components, Formio } from "formiojs";
import FieldComponent from "formiojs/components/_classes/field/Field";

export class FaceRegisterComponent extends FieldComponent {
  private videoStream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private isCameraActive: boolean = false;
  private isProcessing: boolean = false;
  private apiUrl: string = 'http://localhost:3000';
  element: any;
  setValue: any;

  static schema(...extend: any[]) {
    return FieldComponent.schema({
      type: 'faceRegister',
      label: 'Face Register',
      key: 'faceRegister',
      input: true,
      persistent: true
    }, ...extend);
  }

  static builderInfo = {
    title: 'Face Register',
    group: 'basic',
    icon: 'user',
    weight: 100,
    documentation: '',
    schema: FaceRegisterComponent.schema()
  };

  render() {
    return super.render(`
      <div class="mb-2">
        <div ref="faceRegisterContainer" class="face-register-container">
          <div class="video-container">
            <video 
              id="videoElement"
              width="640"
              height="480"
              autoplay 
              playsinline
              style="border: 1px solid #ccc; border-radius: 8px;"
            ></video>
            <div class="camera-status" ref="cameraStatus"></div>
          </div>
          
          <canvas 
            id="canvasElement"
            width="640"
            height="480"
            style="display: none;"
          ></canvas>

          <div class="controls mt-3">
            <div class="form-group mb-3">
              <input 
                type="text"
                class="form-control"
                ref="nameInput"
                placeholder="Enter name"
              />
            </div>

            <div class="form-group mb-3">
              <select class="form-control" ref="systemType">
                <option value="Visitor">Visitor</option>
                <option value="Employee">Employee</option>
                <option value="Labour">Labour</option>
              </select>
            </div>

            <div class="btn-group">
              <button 
                ref="captureBtn"
                class="btn btn-primary"
                disabled
              >
                📸 Register Face
              </button>
              <button 
                ref="resetBtn"
                class="btn btn-secondary"
                disabled
              >
                Reset
              </button>
            </div>
          </div>

          <div ref="preview" class="mt-3"></div>
          <div ref="error" class="text-danger mt-2"></div>
        </div>
      </div>
    `);
  }

  attach(element: HTMLElement) {
    const superAttach = super.attach(element);

    
    this.videoElement = element.querySelector('#videoElement') as HTMLVideoElement;
    this.canvasElement = element.querySelector('#canvasElement') as HTMLCanvasElement;

    setTimeout(() => {
      this.initializeCamera();
    }, 500);

    const captureBtn = element.querySelector('[ref="captureBtn"]');
    if (captureBtn) {
      captureBtn.addEventListener('click', () => this.captureFace());
    }

    const resetBtn = element.querySelector('[ref="resetBtn"]');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetCapture());
    }

    return superAttach;
  }

  async initializeCamera() {
    try {
      if (this.isCameraActive) {
        return;
      }

      this.showStatus('Requesting camera access...');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        },
        audio: false
      });

      if (this.videoElement) {
        this.videoElement.srcObject = stream;
        this.videoStream = stream;
        this.isCameraActive = true;

        this.videoElement.onloadedmetadata = () => {
          if (this.videoElement) {
            this.videoElement.play()
              .then(() => {
                this.showStatus('Camera active');
                this.enableButtons();
              })
              .catch(err => {
                console.error('Error playing video:', err);
                this.showError('Failed to start video');
              });
          }
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      this.showError('Camera access denied. Please check permissions.');
    }
  }

  private enableButtons() {
    const captureBtn = this.element.querySelector('[ref="captureBtn"]') as HTMLButtonElement;
    const resetBtn = this.element.querySelector('[ref="resetBtn"]') as HTMLButtonElement;
    
    if (captureBtn) captureBtn.disabled = false;
    if (resetBtn) resetBtn.disabled = false;
  }

  private disableButtons() {
    const captureBtn = this.element.querySelector('[ref="captureBtn"]') as HTMLButtonElement;
    const resetBtn = this.element.querySelector('[ref="resetBtn"]') as HTMLButtonElement;
    
    if (captureBtn) captureBtn.disabled = true;
    if (resetBtn) resetBtn.disabled = true;
  }

  async captureFace() {
    if (this.isProcessing || !this.videoElement || !this.canvasElement) {
      return;
    }

    const nameInput = this.element.querySelector('[ref="nameInput"]') as HTMLInputElement;
    const systemSelect = this.element.querySelector('[ref="systemType"]') as HTMLSelectElement;
    const name = nameInput?.value?.trim();
    const system = systemSelect?.value;

    if (!name) {
      this.showError('Please enter a name');
      return;
    }

    this.isProcessing = true;
    this.disableButtons();
    this.showStatus('Processing...');

    try {
      const faceData = await this.prepareFaceData(name, system);
      
      const isRegistered = await this.checkFaceExists(faceData.Descriptors);
      
      if (isRegistered) {
        this.showError('This face is already registered');
        return;
      }

      const response = await this.registerFace(faceData);
      
      if (response.id) {
        this.handleSuccessfulRegistration(response, name);
      } else {
        this.showError('Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      this.showError('Registration failed');
    } finally {
      this.isProcessing = false;
      this.enableButtons();
    }
  }

  private async prepareFaceData(name: string, system: string) {
    const ctx = this.canvasElement!.getContext('2d')!;
    this.canvasElement!.width = this.videoElement!.videoWidth;
    this.canvasElement!.height = this.videoElement!.videoHeight;
    
    ctx.drawImage(
      this.videoElement!, 
      0, 0, 
      this.canvasElement!.width, 
      this.canvasElement!.height
    );

    return {
      Reference_Code: 100,
      Page_Code: 424,
      Name: name,
      System: system,
      Descriptors: JSON.stringify(await this.extractFaceDescriptors()),
      ISCommon: 1,
      LogedInID: 424,
      ISActiveStatus: 1,
      RequireType: 0,
      ActionType: 0,
      UserID: 424,
      timestamp: new Date().toISOString()
    };
  }

  private async extractFaceDescriptors() {
    return Array(128).fill(0).map(() => Math.random() - 0.5);
  }

  private async checkFaceExists(descriptors: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/faces`);
      const faces = await response.json();
      return faces.some((face: any) => face.Descriptors === descriptors);
    } catch (error) {
      console.error('Error checking face:', error);
      throw error;
    }
  }

  private async registerFace(faceData: any) {
    try {
      const response = await fetch(`${this.apiUrl}/faces`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(faceData)
      });
      return await response.json();
    } catch (error) {
      console.error('Error registering face:', error);
      throw error;
    }
  }

  private handleSuccessfulRegistration(faceData: any, name: string) {
    const dataUrl = this.canvasElement!.toDataURL('image/png');
    const preview = this.element.querySelector('[ref="preview"]');
    
    if (preview) {
      preview.innerHTML = `
        <div class="mt-3">
          <p class="text-success">Successfully registered: ${name}</p>
          <img 
            src="${dataUrl}" 
            width="320" 
            height="240" 
            style="border-radius: 8px; border: 1px solid #ccc;"
            alt="Captured face"
          />
        </div>`;
    }

    this.setValue(faceData);
    this.showStatus('Registration successful!');
    
    const nameInput = this.element.querySelector('[ref="nameInput"]') as HTMLInputElement;
    if (nameInput) {
      nameInput.value = '';
    }
  }

  resetCapture() {
    this.setValue(null);
    const preview = this.element.querySelector('[ref="preview"]');
    if (preview) {
      preview.innerHTML = '';
    }
    const nameInput = this.element.querySelector('[ref="nameInput"]') as HTMLInputElement;
    if (nameInput) {
      nameInput.value = '';
    }
    this.showStatus('Ready to register');
  }

  showStatus(message: string) {
    const status = this.element.querySelector('.camera-status');
    const error = this.element.querySelector('[ref="error"]');
    
    if (status) {
      status.innerHTML = `<span class="text-info">${message}`;
    }
    if (error) {
      error.innerHTML = '';
    }
  }

  showError(message: string) {
    const error = this.element.querySelector('[ref="error"]');
    if (error) {
      error.innerHTML = `<span class="text-danger">⚠️ ${message}`;
    }
  }

  destroy() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.isCameraActive = false;
    }
    super.destroy();
  }
}

Formio.use({
  components: {
    faceRegister: FaceRegisterComponent
  }
});
